import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useAuth } from '../context/AuthContext'
import { useVendorAuth } from '../context/VendorAuthContext'
import ReactSlider from 'react-slider'
import { fetchCoupons } from '../state/slices/couponSlice';
import { selectAllCoupons } from '../state/selectors';
import { fetchBookmarks, addBookmark, removeBookmark } from '../state/slices/bookmarkSlice';

const CATEGORIES = [
    "Attractions", "Beer / Drinks", "Accommodations", "Coffee Shops",
    "Dining", "Retail", "Transportation", "Golf / Mini",
    "Tours / Excursions", "Spa / Health", "Museums", "Misc",
];

const ShopSection = () => {

    let [grid, setGrid] = useState(false)
    let [active, setActive] = useState(false)
    const [sliderValues, setSliderValues] = useState([0, 100]);
    const [appliedSavings, setAppliedSavings] = useState(null);
    const [activeCity, setActiveCity] = useState(null);
    const [activeRating, setActiveRating] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [categoryFeaturedCoupons, setCategoryFeaturedCoupons] = useState([]);
    const ITEMS_PER_PAGE = 12;
    const navigate = useNavigate()
    const location = useLocation();
    const locationState = location.state;
    const activeCategory = typeof locationState === 'string' ? locationState : (locationState?.category || null);
    const searchQuery = typeof locationState === 'object' && locationState !== null ? (locationState.searchQuery || '') : '';
    const { isAuthenticated, isSubscribed, token } = useAuth();
    const { isVendorAuthenticated } = useVendorAuth();
    const dispatch = useDispatch();
    const coupons = useSelector(selectAllCoupons);

    let sidebarController = () => {
        setActive(!active)
    }

    const maxSavings = coupons.length > 0
        ? Math.ceil(Math.max(...coupons.map(c => parseFloat(c.dollar_savings_amount) || 0)))
        : 100;

    const uniqueCities = [...new Set(coupons.map(c => c.city).filter(Boolean))].sort();

    const scoreSearch = (title, query) => {
        if (!query) return 1;
        const t = title.toLowerCase();
        const q = query.toLowerCase();
        if (t === q) return 4;
        if (t.startsWith(q)) return 3;
        if (t.includes(q)) return 2;
        if (t.split(/\s+/).some(word => word.startsWith(q))) return 1;
        return 0;
    };

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const displayedCoupons = coupons
        .filter(c => {
            if (!activeCategory) return true;
            if (activeCategory === "Newly Added Deals") return c.created_at && new Date(c.created_at) >= thirtyDaysAgo;
            return c.discount_category === activeCategory;
        })
        .filter(c => !activeCity || c.city === activeCity)
        .filter(c => {
            if (!appliedSavings) return true;
            const amount = parseFloat(c.dollar_savings_amount) || 0;
            return amount >= appliedSavings[0] && amount <= appliedSavings[1];
        })
        .filter(c => !activeRating || Math.round(parseFloat(c.average_rating) || 0) >= activeRating)
        .filter(c => !searchQuery || scoreSearch(c.title || '', searchQuery) > 0)
        .sort((a, b) => searchQuery ? scoreSearch(b.title || '', searchQuery) - scoreSearch(a.title || '', searchQuery) : 0);

    const couponsForCategoryCounts = coupons
        .filter(c => !activeCity || c.city === activeCity)
        .filter(c => {
            if (!appliedSavings) return true;
            const amount = parseFloat(c.dollar_savings_amount) || 0;
            return amount >= appliedSavings[0] && amount <= appliedSavings[1];
        });

    const categoryCounts = couponsForCategoryCounts.reduce((acc, c) => {
        acc[c.discount_category] = (acc[c.discount_category] || 0) + 1;
        return acc;
    }, {});

    const ratingCounts = couponsForCategoryCounts.reduce((acc, c) => {
        const rounded = Math.round(parseFloat(c.average_rating) || 0);
        if (rounded >= 1 && rounded <= 5) acc[rounded] = (acc[rounded] || 0) + 1;
        return acc;
    }, {});
    const maxRatingCount = Math.max(...[1,2,3,4,5].map(s => ratingCounts[s] || 0), 1);

    const totalPages = Math.max(1, Math.ceil(displayedCoupons.length / ITEMS_PER_PAGE));
    const pagedCoupons = displayedCoupons.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    const WINDOW_SIZE = 8;
    const windowStart = Math.max(1, Math.min(currentPage - 3, totalPages - WINDOW_SIZE + 1));
    const windowEnd = Math.min(totalPages, windowStart + WINDOW_SIZE - 1);
    const visiblePages = Array.from({ length: windowEnd - windowStart + 1 }, (_, i) => windowStart + i);

    const handlePageClick = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCouponNavigateClick = (id) => {
        console.log("couponid", id)
        navigate(`/coupon/${id}`)
    }


    const bookmarkedIds = useSelector((s) => s.bookmarks.bookmarkedIds);

    useEffect(() => {
        dispatch(fetchCoupons());
    }, [dispatch]);

    useEffect(() => {
        if (isAuthenticated && token) dispatch(fetchBookmarks(token));
    }, [isAuthenticated, token, dispatch]);

    const handleBookmarkToggle = (e, couponId) => {
        e.stopPropagation();
        if (!isAuthenticated) { navigate('/account-login'); return; }
        if (bookmarkedIds.includes(couponId)) {
            dispatch(removeBookmark({ couponId, token }));
        } else {
            dispatch(addBookmark({ couponId, token }));
        }
    };

    useEffect(() => {
        if (!activeCategory) {
            setCategoryFeaturedCoupons([]);
            return;
        }
        const url = activeCategory === "Featured"
            ? `${process.env.REACT_APP_API_URL}/api/coupons/get_category_featured_coupons`
            : `${process.env.REACT_APP_API_URL}/api/coupons/get_category_featured_coupons?category=${encodeURIComponent(activeCategory)}`;
        fetch(url)
            .then(r => r.ok ? r.json() : [])
            .then(data => setCategoryFeaturedCoupons(Array.isArray(data) ? data : []))
            .catch(() => setCategoryFeaturedCoupons([]));
    }, [activeCategory]);

    useEffect(() => {
        if (coupons.length > 0) {
            const max = Math.ceil(Math.max(...coupons.map(c => parseFloat(c.dollar_savings_amount) || 0)));
            setSliderValues([0, max]);
        }
    }, [coupons.length]);

    useEffect(() => {
        setCurrentPage(1);
    }, [activeCategory, activeCity, appliedSavings, activeRating, searchQuery]);




    return (
        <section className="shop py-80">
            <div className={`side-overlay ${active && "show"}`}></div>
            <div className="container container-lg">
                <div className="row">
                    {/* Sidebar Start */}
                    <div className="col-lg-3">
                        <div className={`shop-sidebar ${active && "active"}`}>
                            <button onClick={sidebarController}
                                type="button"
                                className="shop-sidebar__close d-lg-none d-flex w-32 h-32 flex-center border border-main-600 bg-main-600 text-white rounded-circle hover-bg-main-700 position-absolute inset-inline-end-0 me-10 mt-8 hover-text-white hover-border-main-700"
                            >
                                <i className="ph ph-x" />
                            </button>
                            <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                                <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">
                                    Product Category
                                </h6>
                                <ul className="max-h-540 overflow-y-auto scroll-sm">
                                    <li className="mb-24">
                                        <Link
                                            to="/shop"
                                            state="Featured"
                                            className={`hover-text-main-800 d-flex align-items-center gap-6 ${activeCategory === "Featured" ? "text-main-800 fw-semibold" : "text-gray-900"}`}
                                        >
                                            <i className="ph-fill ph-star" style={{ color: "#7c3aed", fontSize: 13 }} />
                                            Featured
                                        </Link>
                                    </li>
                                    <li className="mb-24">
                                        <Link
                                            to="/shop"
                                            state="Newly Added Deals"
                                            className={`hover-text-main-800 d-flex align-items-center gap-6 ${activeCategory === "Newly Added Deals" ? "text-main-800 fw-semibold" : "text-gray-900"}`}
                                        >
                                            <i className="ph-fill ph-sparkle" style={{ color: "#10b981", fontSize: 13 }} />
                                            Newly Added Deals
                                        </Link>
                                    </li>
                                    <li className="mb-24">
                                        <Link
                                            to="/shop"
                                            state={null}
                                            className={`hover-text-main-800 ${!activeCategory ? "text-main-800 fw-semibold" : "text-gray-900"}`}
                                        >
                                            All Categories ({couponsForCategoryCounts.length})
                                        </Link>
                                    </li>
                                    {CATEGORIES.map((cat) => (
                                        <li key={cat} className="mb-24">
                                            <Link
                                                to="/shop"
                                                state={cat}
                                                className={`hover-text-main-800 ${activeCategory === cat ? "text-main-800 fw-semibold" : "text-gray-900"}`}
                                            >
                                                {cat} ({categoryCounts[cat] || 0})
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                                <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">
                                    Filter by Savings Amount
                                </h6>
                                <div className="custom--range">
                                    <div className="flex-between mb-12">
                                        <span className="text-sm text-gray-800">${sliderValues[0]}</span>
                                        <span className="text-sm text-gray-800">${sliderValues[1]}</span>
                                    </div>
                                    <ReactSlider
                                        className="horizontal-slider"
                                        thumbClassName="example-thumb"
                                        trackClassName="example-track"
                                        value={sliderValues}
                                        min={0}
                                        max={maxSavings}
                                        ariaLabel={['Lower thumb', 'Upper thumb']}
                                        ariaValuetext={state => `$${state.valueNow}`}
                                        renderThumb={(props, state) => {
                                            const { key, ...restProps } = props;
                                            return <div {...restProps} key={state.index}>${state.valueNow}</div>;
                                        }}
                                        onChange={(values) => setSliderValues(values)}
                                        pearling
                                        minDistance={1}
                                    />
                                    <br />
                                    <br />
                                    <div className="flex-between flex-wrap-reverse gap-8 mt-24">
                                        <button
                                            type="button"
                                            className="btn bg-main-800 text-white hover-bg-main-900 h-40 flex-align"
                                            onClick={() => setAppliedSavings(sliderValues)}
                                        >
                                            Filter
                                        </button>
                                        {appliedSavings && (
                                            <button
                                                type="button"
                                                className="btn btn-outline-main h-40 flex-align"
                                                onClick={() => {
                                                    setAppliedSavings(null);
                                                    setSliderValues([0, maxSavings]);
                                                }}
                                            >
                                                Clear
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                                <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">
                                    Filter by Rating
                                </h6>
                                {[5,4,3,2,1].map((stars, idx) => (
                                    <div
                                        key={stars}
                                        className={`flex-align gap-8 position-relative ${idx < 4 ? 'mb-20' : 'mb-0'}`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setActiveRating(activeRating === stars ? null : stars)}
                                    >
                                        <div className="common-check common-radio mb-0">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="rating"
                                                id={`rating${stars}`}
                                                checked={activeRating === stars}
                                                onChange={() => setActiveRating(activeRating === stars ? null : stars)}
                                            />
                                        </div>
                                        <div
                                            className="progress w-100 bg-gray-100 rounded-pill h-8"
                                            role="progressbar"
                                            aria-valuemin={0}
                                            aria-valuemax={100}
                                            aria-valuenow={Math.round(((ratingCounts[stars] || 0) / maxRatingCount) * 100)}
                                        >
                                            <div
                                                className="progress-bar bg-main-600 rounded-pill"
                                                style={{ width: `${Math.round(((ratingCounts[stars] || 0) / maxRatingCount) * 100)}%` }}
                                            />
                                        </div>
                                        <div className="flex-align gap-4">
                                            {[1,2,3,4,5].map(s => (
                                                <span key={s} className={`text-xs fw-medium d-flex ${s <= stars ? 'text-warning-600' : 'text-gray-400'}`}>
                                                    <i className="ph-fill ph-star" />
                                                </span>
                                            ))}
                                        </div>
                                        <span className="text-gray-900 flex-shrink-0">({ratingCounts[stars] || 0})</span>
                                    </div>
                                ))}
                                {activeRating && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-main h-32 flex-align mt-20 text-sm"
                                        onClick={e => { e.stopPropagation(); setActiveRating(null); }}
                                    >
                                        Clear Rating
                                    </button>
                                )}
                            </div>
                            <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                                <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">
                                    Filter by Location
                                </h6>
                                <ul className="max-h-540 overflow-y-auto scroll-sm">
                                    <li className="mb-16">
                                        <div className="form-check common-check common-radio">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="city"
                                                id="city-all"
                                                checked={activeCity === null}
                                                onChange={() => setActiveCity(null)}
                                            />
                                            <label className="form-check-label" htmlFor="city-all">
                                                All Locations
                                            </label>
                                        </div>
                                    </li>
                                    {uniqueCities.map((city) => (
                                        <li key={city} className="mb-16">
                                            <div className="form-check common-check common-radio">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="city"
                                                    id={`city-${city}`}
                                                    checked={activeCity === city}
                                                    onChange={() => setActiveCity(city)}
                                                />
                                                <label className="form-check-label" htmlFor={`city-${city}`}>
                                                    {city}
                                                </label>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            {/* <div className="shop-sidebar__box rounded-8">
                                <img src="assets/images/thumbs/advertise-img1.png" alt="" />
                            </div> */}
                        </div>
                    </div>
                    {/* Sidebar End */}
                    {/* Content Start */}
                    <div className="col-lg-9">
                        {/* Top Start */}
                        <div className="flex-between gap-16 flex-wrap mb-40 ">
                            <span className="text-gray-900">
                                {searchQuery
                                    ? `${displayedCoupons.length} result${displayedCoupons.length !== 1 ? "s" : ""} for "${searchQuery}"`
                                    : activeCategory === "Featured"
                                        ? `${categoryFeaturedCoupons.length} featured coupon${categoryFeaturedCoupons.length !== 1 ? "s" : ""}`
                                        : activeCategory
                                            ? `${displayedCoupons.length + categoryFeaturedCoupons.length} result${(displayedCoupons.length + categoryFeaturedCoupons.length) !== 1 ? "s" : ""} in "${activeCategory}"`
                                            : `Showing all ${displayedCoupons.length} coupons`}
                            </span>
                            <div className="position-relative flex-align gap-16 flex-wrap">
                                <div className="list-grid-btns flex-align gap-16">
                                    <button onClick={() => setGrid(true)}
                                        type="button"
                                        className={`w-44 h-44 flex-center border rounded-6 text-2xl list-btn border-gray-100 ${grid === true && "border-main-600 text-white bg-main-600"}`}
                                    >
                                        <i className="ph-bold ph-list-dashes" />
                                    </button>
                                    <button onClick={() => setGrid(false)}
                                        type="button"
                                        className={`w-44 h-44 flex-center border rounded-6 text-2xl grid-btn border-gray-100 ${grid === false && "border-main-600 text-white bg-main-600"}`}
                                    >
                                        <i className="ph ph-squares-four" />
                                    </button>
                                </div>
                                <button onClick={sidebarController}
                                    type="button"
                                    className="w-44 h-44 d-lg-none d-flex flex-center border border-gray-100 rounded-6 text-2xl sidebar-btn"
                                >
                                    <i className="ph-bold ph-funnel" />
                                </button>
                            </div>
                        </div>
                        {/* Top End */}

                        {/* Category-Featured Showcase */}
                        {categoryFeaturedCoupons.length > 0 && (
                            <div className="mb-40">
                                <div className="d-flex align-items-center gap-10 mb-20">
                                    <span className="px-12 py-4 rounded-pill fw-semibold text-white" style={{ fontSize: 12, background: "#7c3aed" }}>
                                        <i className="ph ph-star me-4" />
                                        {activeCategory === "Featured" ? "All Featured Coupons" : `Featured in ${activeCategory}`}
                                    </span>
                                    <span className="text-gray-400 text-sm">
                                        {activeCategory === "Featured" ? "Category-featured picks across all categories" : "Top picks for this category"}
                                    </span>
                                </div>
                                <div className="row g-12">
                                    {categoryFeaturedCoupons.map((coupon) => (
                                        <div key={coupon.id} className="col-md-3 col-sm-6">
                                            <div
                                                className="product-card p-16 rounded-16 position-relative transition-2"
                                                style={{ border: "2px solid #7c3aed", background: "#faf5ff", height: '500px', display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, width: '100%' }}
                                            >
                                                <div
                                                    onClick={() => handleCouponNavigateClick(coupon.id)}
                                                    className="product-card__thumb flex-center rounded-8 bg-gray-50 position-relative"
                                                    style={{ overflow: 'hidden', cursor: 'pointer', flexShrink: 0 }}
                                                >
                                                    {/* <span className="product-card__badge px-8 py-4 text-sm text-white position-absolute inset-inline-start-0 inset-block-start-0" style={{ background: "#7c3aed" }}>
                                                        Category Featured
                                                    </span> */}
                                                    {coupon.image_url ? (
                                                        <img
                                                            src={coupon.image_url}
                                                            alt=""
                                                            style={{ width: '100%', height: '100%', maxWidth: 'none', maxHeight: 'none', objectFit: 'cover', position: 'absolute', inset: 0 }}
                                                        />
                                                    ) : (
                                                        <div style={{ width: '100%', height: '100%', background: '#f3f4f6', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'absolute', inset: 0 }}>
                                                            <i className="ph ph-image" style={{ fontSize: 36, color: '#d1d5db' }} />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="product-card__content w-100 mt-16" style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minWidth: 0 }}>
                                                    <div style={{ height: '24px', flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
                                                        {coupon.business_name ? (
                                                            <>
                                                                <span className="text-tertiary-600 text-sm d-flex" style={{ flexShrink: 0 }}><i className="ph-fill ph-storefront" /></span>
                                                                <span className="text-lg fw-semibold text-gray-500" style={{ fontFamily: "var(--heading-font)", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', minWidth: 0 }}>{coupon.business_name}</span>
                                                            </>
                                                        ) : null}
                                                    </div>
                                                    <div className="flex-align gap-6 mb-8" style={{ flexShrink: 0 }}>
                                                        <div className="flex-align gap-4">
                                                            {[1,2,3,4,5].map(star => (
                                                                <span key={star} className={`text-xs fw-medium d-flex ${star <= Math.round(parseFloat(coupon.average_rating) || 0) ? 'text-warning-600' : 'text-gray-300'}`}>
                                                                    <i className="ph-fill ph-star" />
                                                                </span>
                                                            ))}
                                                        </div>
                                                        <span className="text-xs fw-medium text-gray-500">
                                                            {coupon.average_rating ? parseFloat(coupon.average_rating).toFixed(1) : "—"}
                                                        </span>
                                                        <span className="text-xs text-gray-400">({coupon.rating_count ?? 0})</span>
                                                    </div>
                                                    <h6 className="title text-lg fw-semibold mt-8 mb-8" style={{ flexShrink: 0 }}>
                                                        <div onClick={() => handleCouponNavigateClick(coupon.id)} className="link d-block" style={{ cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {coupon.title}
                                                        </div>
                                                    </h6>
                                                    <p className="text-sm text-gray-500 mb-8" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 0, minHeight: '20px' }}>
                                                        {coupon.description || ' '}
                                                    </p>
                                                    <div style={{ marginTop: 'auto', flexShrink: 0 }}>
                                                        <div className="d-flex align-items-center gap-8 mb-12">
                                                            <span className="fw-semibold text-main-600">${coupon.dollar_savings_amount} savings</span>
                                                            <span className="text-gray-400">·</span>
                                                            <span className="text-gray-500 text-sm">{coupon.saving_percentage}% off</span>
                                                        </div>
                                                        {!isVendorAuthenticated && (
                                                            isAuthenticated && isSubscribed ? (
                                                                <Link
                                                                    to={`/coupon/${coupon.id}`}
                                                                    className="btn w-100 py-10 px-16 rounded-8 flex-center gap-8 fw-medium text-white"
                                                                    style={{ background: "#7c3aed", fontSize: 13 }}
                                                                >
                                                                    Redeem Coupon
                                                                </Link>
                                                            ) : (
                                                                <Link
                                                                    to={isAuthenticated ? "/cart" : "/account-login"}
                                                                    state={{ from: `/coupon/${coupon.id}` }}
                                                                    className="btn w-100 py-10 px-16 rounded-8 flex-center gap-8 fw-medium text-white"
                                                                    style={{ background: "#7c3aed", fontSize: 13 }}
                                                                >
                                                                    Buy Fun Card
                                                                </Link>
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <hr className="mt-40 mb-0 border-gray-100" />
                            </div>
                        )}

                        {activeCategory !== "Featured" && <div className={`list-grid-wrapper grid-cols-4 ${grid && "list-view"}`}>
                            {
                                Array.isArray(pagedCoupons) && pagedCoupons.length > 0 ? (
                                    pagedCoupons.map((coupon, key) => {
                                        return (
                                    <div className="product-card p-16 border border-gray-100 hover-border-main-600 rounded-16 position-relative transition-2" style={{ height: !grid ? '500px' : undefined, display: 'flex', flexDirection: grid ? 'row' : 'column', overflow: 'hidden', minWidth: 0, width: '100%' }}>
                                        <div
                                            onClick={() => handleCouponNavigateClick(coupon.id)}
                                            className="product-card__thumb flex-center rounded-8 bg-gray-50 position-relative"
                                            style={{ overflow: 'hidden', cursor: 'pointer', flexShrink: 0 }}
                                        >
                                            <button
                                                type="button"
                                                onClick={(e) => handleBookmarkToggle(e, coupon.id)}
                                                style={{ position: "absolute", top: 8, right: 8, zIndex: 3, background: "rgba(255,255,255,0.85)", border: "none", borderRadius: "50%", width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", backdropFilter: "blur(4px)", boxShadow: "0 1px 4px rgba(0,0,0,0.12)" }}
                                                title={bookmarkedIds.includes(coupon.id) ? "Remove bookmark" : "Bookmark"}
                                            >
                                                <i className={bookmarkedIds.includes(coupon.id) ? "ph-fill ph-bookmark-simple" : "ph ph-bookmark-simple"} style={{ fontSize: 16, color: bookmarkedIds.includes(coupon.id) ? "#2575fc" : "#9ca3af" }} />
                                            </button>
                                            {coupon.image_url ? (
                                                <img
                                                    src={coupon.image_url}
                                                    alt=""
                                                    style={{ width: '100%', height: '100%', maxWidth: 'none', maxHeight: 'none', objectFit: 'cover', position: 'absolute', inset: 0 }}
                                                />
                                            ) : (
                                                <div style={{ width: '100%', height: '100%', background: '#f3f4f6', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'absolute', inset: 0 }}>
                                                    <i className="ph ph-image" style={{ fontSize: 36, color: '#d1d5db' }} />
                                                </div>
                                            )}
                                            {/* <span className="product-card__badge bg-main-600 px-8 py-4 text-sm text-white position-absolute inset-inline-start-0 inset-block-start-0">
                                                New
                                            </span> */}
                                        </div>
                                        <div className="product-card__content w-100 mt-16" style={{ display: 'flex', flexDirection: 'column', flex: !grid ? 1 : undefined, overflow: 'hidden', minWidth: 0 }}>
                                            <div style={{ height: '24px', flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
                                                {coupon.business_name ? (
                                                    <>
                                                        <span className="text-tertiary-600 text-sm d-flex" style={{ flexShrink: 0 }}><i className="ph-fill ph-storefront" /></span>
                                                        <span className="text-lg fw-semibold text-gray-500" style={{ fontFamily: "var(--heading-font)", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', minWidth: 0 }}>{coupon.business_name}</span>
                                                    </>
                                                ) : null}
                                            </div>
                                            <div className="flex-align gap-6" style={{ flexShrink: 0 }}>
                                                <div className="flex-align gap-4">
                                                    {[1,2,3,4,5].map(star => (
                                                        <span key={star} className={`text-xs fw-medium d-flex ${star <= Math.round(coupon.average_rating || 0) ? 'text-warning-600' : 'text-gray-300'}`}>
                                                            <i className="ph-fill ph-star" />
                                                        </span>
                                                    ))}
                                                </div>
                                                <span className="text-xs fw-medium text-gray-500">{coupon.average_rating ?? "—"}</span>
                                                <span className="text-xs fw-medium text-gray-500">({coupon.rating_count ?? 0})</span>
                                            </div>
                                            <h6 className="title text-lg fw-semibold mt-12 mb-8" style={{ flexShrink: 0 }}>
                                                <div
                                                    onClick={() => handleCouponNavigateClick(coupon.id)}
                                                    className="link d-block"
                                                    tabIndex={0}
                                                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                                >
                                                    {coupon.title}
                                                </div>
                                            </h6>
                                            <p className="text-sm text-gray-500 mb-8" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 0, minHeight: '20px' }}>
                                                {coupon.description || ' '}
                                            </p>
                                            <div style={!grid ? { marginTop: 'auto', flexShrink: 0 } : {}}>
                                                <div className="product-card__price flex-align gap-4 mt-12 mb-12">
                                                    <span className="text-heading text-md fw-semibold">
                                                        ${coupon.dollar_savings_amount}
                                                    </span>
                                                    <span className="text-gray-500 text-sm fw-medium">In Savings</span>
                                                    <span style={{ width: '1px', height: '14px', backgroundColor: '#d1d5db', display: 'inline-block', margin: '0 4px' }} />
                                                    <span className="text-main-800 text-sm fw-semibold">{coupon.saving_percentage}% Off</span>
                                                </div>
                                                {!isVendorAuthenticated && (
                                                    isAuthenticated && isSubscribed ? (
                                                        <Link
                                                            to={`/coupon/${coupon.id}`}
                                                            className="product-card__cart w-100 btn bg-main-800 text-white hover-bg-main-700 hover-text-white py-11 px-24 rounded-8 flex-center gap-8 fw-medium w-100"
                                                        >
                                                            Redeem Coupon <i className="ph ph-shopping-cart" />
                                                        </Link>
                                                    ) : (
                                                        <Link
                                                            to={isAuthenticated ? "/cart" : "/account-login"}
                                                            state={{ from: `/coupon/${coupon.id}` }}
                                                            className="product-card__cart w-100 btn bg-main-800 text-white hover-bg-main-700 hover-text-white py-11 px-24 rounded-8 flex-center gap-8 fw-medium w-100"
                                                        >
                                                            Buy Fun Card <i className="ph ph-shopping-cart" />
                                                        </Link>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    )
                                        })
                                    ) : (
                                        <div>No coupons available</div>
                                    )
                                }
                        </div>}
                        {/* Pagination Start */}
                        {activeCategory !== "Featured" && totalPages > 1 && (
                            <ul className="pagination flex-center flex-wrap gap-16 mt-40">
                                <li className="page-item">
                                    <button
                                        type="button"
                                        disabled={currentPage === 1}
                                        onClick={() => handlePageClick(currentPage - 1)}
                                        className="page-link h-64 w-64 flex-center text-xxl rounded-8 fw-medium text-neutral-600 border border-gray-100 bg-transparent"
                                        style={{ opacity: currentPage === 1 ? 0.4 : 1, cursor: currentPage === 1 ? 'default' : 'pointer' }}
                                    >
                                        <i className="ph-bold ph-arrow-left" />
                                    </button>
                                </li>
                                {visiblePages.map(page => (
                                    <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                        <button
                                            type="button"
                                            onClick={() => handlePageClick(page)}
                                            className={`page-link h-64 w-64 flex-center text-md rounded-8 fw-medium border ${currentPage === page ? 'bg-main-600 text-white border-main-600' : 'text-neutral-600 border-gray-100 bg-transparent'}`}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {String(page).padStart(2, '0')}
                                        </button>
                                    </li>
                                ))}
                                <li className="page-item">
                                    <button
                                        type="button"
                                        disabled={currentPage === totalPages}
                                        onClick={() => handlePageClick(currentPage + 1)}
                                        className="page-link h-64 w-64 flex-center text-xxl rounded-8 fw-medium text-neutral-600 border border-gray-100 bg-transparent"
                                        style={{ opacity: currentPage === totalPages ? 0.4 : 1, cursor: currentPage === totalPages ? 'default' : 'pointer' }}
                                    >
                                        <i className="ph-bold ph-arrow-right" />
                                    </button>
                                </li>
                            </ul>
                        )}
                        {/* Pagination End */}
                    </div>
                    {/* Content End */}
                </div>
            </div>
        </section>

    )
}

export default ShopSection